import moment from 'moment';
import { formatRoute } from 'react-router-named-routes';
import { PAID_AD, PROMOTED_AD } from 'routes/paths';
import { paidAdTypes, publicImageUrl } from 'config';

/**
 * @description
 * Allow user to change currency in a PaidAd
 *
 * @param {Array} allPermissions - User permissions.
 * @returns {boolean} User is allowed.
 */
export const allowPaidAdChangeCurrency = allPermissions =>
  allPermissions.some(
    ({ codename }) =>
      codename === 'change_currency_autopaidad' ||
      codename === 'change_currency_ownership_autopaidad' ||
      codename === 'change_currency_promotedad' ||
      codename === 'change_currency_ownership_promotedad',
  ) &&
  allPermissions.some(
    ({ codename }) => codename === 'view_current_exchangerate',
  );

/**
 * @description
 * Format frequency and duration received from the server.
 *
 * @param {Object} param - Frequency or duration received from the server.
 * @param {Object} i18n - Translation.
 * @returns {Object} Converted and translated frequency and duration.
 */
const changeFormatToString = (
  { years, months, days, hours, minutes, seconds },
  i18n,
) => {
  moment.locale(i18n.language);
  return moment()
    .add({ seconds, minutes, hours, days, months, years })
    .fromNow(true);
};

const ReadableDate = ({ years, months, days, hours, minutes, seconds }) =>
  moment()
    .add({ seconds, minutes, hours, days, months, years })
    .fromNow(true);

/**
 * @description
 * Get paid ad details url.
 *
 * @param {string} id - Ad id.
 * @param {string} paidAdType - Type.
 * @returns {string} - Url.
 */
export const getPaidAdDetailsUrl = (id, paidAdType) => {
  switch (paidAdType) {
    case paidAdTypes.AUTO_PAID_AD:
      return formatRoute(PAID_AD.DETAILS, { id });
    case paidAdTypes.PROMOTED_AD:
      return formatRoute(PROMOTED_AD.DETAILS, { id });
    default:
      return '';
  }
};

/**
 * @description
 * Get paid ad title.
 *
 * @param {string} id - Ad id.
 * @param {string} paidAdType - Type.
 * @param {*} t - Translations.
 * @returns {string} - Title.
 */
export const getPaidAdTitle = (id, paidAdType, t) => {
  switch (paidAdType) {
    case paidAdTypes.AUTO_PAID_AD:
      return `${t('paidad.list.headers.auto')}: ${id}`;
    case paidAdTypes.PROMOTED_AD:
      return `${t('promoted_ad.list.headers.promoted')}: ${id}`;
    default:
      return '';
  }
};

/**
 * @description
 * Get paid ad description.
 *
 * @param {Object} paidAd - Paid ad.
 * @param {*} t - Translations.
 * @returns {string} - Description.
 */
export const getPaidAdDescription = (
  { frequency, paidAdType, schedule, locationScope, duration },
  t,
) => {
  let description = '';
  switch (paidAdType) {
    case paidAdTypes.AUTO_PAID_AD:
      description = `${t('paidad.details.frequency', {
        frequency: changeFormatToString(frequency, t),
      })}/${t(`paidad.details.schedule.${schedule}`)}`;
      break;
    case paidAdTypes.PROMOTED_AD:
      description = `${locationScope.charAt(0) +
        locationScope.toLowerCase().slice(1)}`;
      break;
    default:
      break;
  }

  return `${description}/${ReadableDate(duration)}`;
};

/**
 * @description
 * Get image url from user's photo.
 *
 * @param {Object} photo - Photo.
 * @param {string} version - Image version.
 *
 * @returns {string} - Image url.
 */
export const getUserPhotoUrl = (photo, version = 'thumb') =>
  photo.gcsKey
    ? `${publicImageUrl}/${photo.gcsKey}.jpg?class=${version}`
    : photo.url;

/**
 * @description
 * Check if user is public or private
 *
 * @param {Object} user - User object.
 * @returns {boolean} - True if user is public, false otherwise.
 */
export const isUserPublic = user => user && !['0', 0].includes(user.id);
